/**
 * 메일링크 로고 — 챗봇 위젯의 모든 아바타 위치에서 공통 사용
 *
 * size:
 *   sm — Bubble, TypingIndicator (26x26 박스, 18x18 이미지)
 *   md — ChatWindow Header     (40x40 박스, 28x28 이미지)
 *
 * 에셋 base 경로 결정 우선순위:
 *   1. VITE_ASSET_BASE_URL (빌드 타임 명시 override)
 *   2. widget.js를 로드한 <script>의 src에서 디렉터리 추출 (런타임 자동 감지)
 *      → '/chatbot/widget.js' 으로 로드되면 base = '/chatbot'
 *   3. 폴백: '' (개발 환경에서 Vite가 public 루트에서 서빙)
 */

import { cn } from '@/lib/utils';

interface LogoProps {
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE_MAP = {
  sm: { box: 26, img: 18 },
  md: { box: 40, img: 28 },
};

function resolveAssetBase(): string {
  const envBase = import.meta.env.VITE_ASSET_BASE_URL;
  if (envBase) return envBase.replace(/\/$/, '');

  if (typeof document !== 'undefined') {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[src*="widget.js"]'
    );
    const last = scripts[scripts.length - 1];
    if (last?.src) {
      return last.src
        .replace(/\?.*$/, '') // 쿼리스트링(?v=...) 제거
        .replace(/\/[^/]*$/, ''); // 파일명 제거 → 디렉터리만 남김
    }
  }

  return '';
}

const ASSET_BASE = resolveAssetBase();
const LOGO_SRC = `${ASSET_BASE}/maillink_symbol_512.png`;

export function Logo({ size = 'sm', className }: LogoProps) {
  const dim = SIZE_MAP[size];
  const isHeader = size === 'md';

  return (
    <div
      className={cn(
        'ml-flex ml-items-center ml-justify-center ml-flex-shrink-0',
        'ml-rounded-cb-sm ml-overflow-hidden',
        isHeader
          ? 'ml-bg-white/20 ml-border ml-border-white/30'
          : 'ml-bg-gradient-to-br ml-from-brand-600 ml-to-brand-500',
        className
      )}
      style={{ width: dim.box, height: dim.box }}
    >
      <img
        src={LOGO_SRC}
        alt="메일링크"
        loading="lazy"
        style={{
          width: dim.img,
          height: dim.img,
          objectFit: 'contain',
        }}
        onError={(e) => {
          // 이미지 로드 실패 시 'ML' 텍스트로 폴백
          const target = e.currentTarget;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent && !parent.querySelector('.ml-logo-fallback')) {
            const span = document.createElement('span');
            span.className = 'ml-logo-fallback';
            span.textContent = 'ML';
            span.style.color = 'white';
            span.style.fontSize = isHeader ? '13px' : '10px';
            span.style.fontWeight = '600';
            parent.appendChild(span);
          }
        }}
      />
    </div>
  );
}
